import * as React from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { TableHead } from '@mui/material'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import ResetTemporaryPassword from '../../../../components/dialogs/ResetTemporaryPassword'
import ArrowRightIcon from '../../../../assets/icons/Arrows-chevron/Arrow-Right.svg'
import { capitalize } from '../../../../utils/functions'
import { CuratorStudentState } from '../../../../features/types'
import ProgressBar from '../../../../components/progressBar/ProgressBar'
import { tableStyles, ui } from '../../../../utils/uiTokens'
import styled from '@emotion/styled'
import HistoryIcon from '@mui/icons-material/History';

interface Props {
    studentsData: CuratorStudentState[]
    isFetching: boolean
    onResetPassword?: (id: number) => void
}

const StudentsTable: React.FC<Props> = ({
    studentsData = [],
    isFetching,
    onResetPassword,
}) => {
    const { formatMessage, locale } = useIntl()

    const getDuration = (daysLeft?: number) => {
        if (daysLeft > 30) {
            return (
                <>
                    <Medium>
                        {Math.floor(daysLeft / 30) + ' ' + formatMessage({ id: 'month' })}
                    </Medium>
                    <DayLabel>
                        <HistoryIcon sx={{ fontSize: 18 }} />
                        {daysLeft % 30 + ' ' + formatMessage({ id: 'day' })}
                    </DayLabel>
                </>)
        } else if (daysLeft === 0) {
            return <FinishedLabel>✓ {formatMessage({ id: 'courseCompleted' })}</FinishedLabel>
        } else {
            return (
                <DayLabel>
                    <HistoryIcon sx={{ fontSize: 18 }} />
                    {daysLeft + ' ' + formatMessage({ id: 'day' })}
                </DayLabel>
            )
        }
    }

    return (
        <Box>
            <TableContainer
                component={Paper}
                sx={tableStyles.container}
                elevation={0}
            >
                <Table
                    sx={{ minWidth: 500 }}
                    aria-label="custom pagination table"
                >
                    <TableHead sx={tableStyles.head}>
                        <TableRow>
                            <TableCell>#</TableCell>

                            <TableCell>
                                {formatMessage({ id: 'fullName' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'phone' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'temporaryPassword' })}
                            </TableCell>

                            <TableCell align="center">
                                {formatMessage({ id: 'performance' })}
                            </TableCell>

                            <TableCell>
                                {capitalize(
                                    formatMessage({ id: 'videoLesson' }),
                                )}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'test' })}
                            </TableCell>

                            <TableCell align="center">
                                {formatMessage({ id: 'courseDuration' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'details' })}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    {studentsData.length ? (
                        <TableBody>
                            {studentsData.map((row, index) => (
                                <TableRow key={row.userId} sx={tableStyles.row}>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {index + 1}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row.firstName} {row.lastName}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row.mobile}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        <ResetTemporaryPassword
                                            password={row.password}
                                            fullName={
                                                row.lastName +
                                                ' ' +
                                                row.firstName
                                            }
                                            isLoading={isFetching}
                                            confirmAction={() =>
                                                onResetPassword(row.userId)
                                            }
                                        />
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                    >
                                        {row.performanceLevel && <StatusSpan performanceLevel={row.performanceLevel}>{formatMessage({ id: `${row.performanceLevel}` })}</StatusSpan>}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        <ProgressBar value={row.watchedVideoPercentage} />
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        <ProgressBar value={row.topicTestPercentage} />
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                    >
                                        <LabelBox>
                                            {getDuration(row?.daysLeft)}
                                        </LabelBox>
                                    </TableCell>

                                    <TableCell
                                        style={{ width: 60 }}
                                        align="right"
                                    >
                                        <Link to={`/curator/students/${row?.userCourseEnrollId}?fullName=${row?.firstName}-${row?.lastName}`}>
                                            <IconButton
                                                sx={{ background: ui.pinkSoft, borderRadius: '10px' }}
                                            >
                                                <img
                                                    src={ArrowRightIcon}
                                                    alt="edit"
                                                />
                                            </IconButton>
                                        </Link>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    ) : (
                        !isFetching && (
                            <TableBody>
                                <TableRow>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                        colSpan={9}
                                    >
                                        {formatMessage({ id: 'noData' })}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )
                    )}

                </Table>
            </TableContainer>
        </Box>
    )
}

export default StudentsTable


const FinishedLabel = styled.span`
    color: #0D894F;
    font-weight: 500;
    font-size: 16px;
    background-color: #CFE7DC;
    border: 4px solid #E7F4EE;
    border-radius: 100px;
    padding: 2px 12px;
`

const Medium = styled.span`
    font-size: 16px;
    font-weight: 600;
    color: #434343;
`

const LabelBox = styled.div`
    display: flex;
    flex-direction: column;
    text-align: center;
    align-items: center;
`

const DayLabel = styled.span`
    font-size: 14px;
    color: #667085;
    display: flex;
    align-items: center;
    gap: 2px;
`

const StatusSpan = styled.span<{ performanceLevel: string }>`
    background-color: ${({ performanceLevel }) => {
        switch (performanceLevel) {
            case 'ACTIVE':
                return '#E7F4EE';
            case 'MEDIUM':
                return '#E8F8FD';
            case 'LOW':
                return '#FDF1E8';
            case 'NEEDS_HELP':
                return '#FEEDEC';
            default:
                return '#E0E2E7';
        }
    }};
    color: ${({ performanceLevel }) => {
        switch (performanceLevel) {
            case 'ACTIVE':
                return '#1B998B';
            case 'MEDIUM':
                return '#13B2E4';
            case 'LOW':
                return '#E46A11';
            case 'NEEDS_HELP':
                return '#F04438';
            default:
                return '#434343';
        }
    }};
    border-radius: 100px;
    padding: 6px 12px;
    font-weight: 600;
    font-size: 14px;
`